import React, { useRef, useState } from 'react';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import Select from '../../ui/selects/Select';
import { FileText, Upload, X } from 'lucide-react';
import { useToast } from '../../ui/toast/ToastContext';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import ColorSelect from '../../ui/selects/ColorSelect';
import CustomModal from '../CustomModal';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2mb
const ALLOWED_EXTENSIONS = ['xls', 'xlsx', 'csv'];

const AddBaseModal = ({ onClose, onSuccess }) => {
  const { addToast } = useToast();

  const [form, setForm] = useState({
    description: `Importação - ${new Date().toLocaleDateString('pt-BR')}`,
    baseType: '',
    color: '#7D48C7',
  });

  const [fileToUpload, setFileToUpload] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateFile = (file) => {
    if (!file) return { ok: false, message: 'Nenhum arquivo selecionado.' };

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return { ok: false, message: `Formato inválido. Use: ${ALLOWED_EXTENSIONS.join(', ')}` };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { ok: false, message: 'Arquivo muito grande. Máximo: 2mb.' };
    }

    return { ok: true };
  };

  const setFileSafe = (file) => {
    const validation = validateFile(file);
    if (!validation.ok) {
      addToast({ type: 'error', message: validation.message });
      return;
    }
    setFileToUpload(file);
  };

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fileToUpload) {
      addToast({ type: 'error', message: 'Selecione uma planilha para importar.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();
      fd.append('file', fileToUpload);
      fd.append('description', form.description);
      fd.append('base_type', form.baseType);
      fd.append('color', form.color);

      const res = await fetchWithCsrf(`/app/analyst/assets/import_base`, {
        method: 'POST',
        body: fd,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json.error || json.message || 'Erro ao importar base');
      }

      addToast({
        type: 'success',
        message: json.message || 'Base criada e planilha importada com sucesso!',
      });

      onSuccess();
      onClose();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal onClose={onClose}>
      <div className="p-10" style={{ maxWidth: '720px' }}>
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Upload size={24} className="text-tealMedium" strokeWidth={1.25} />
            Adicionar base
          </div>
          <button onClick={onClose} type="button">
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <span className="text-blueSteel font-jost text-base mb-6 block">
          Importe a Planilha de Fundos da BTG Pactual para atualizar a base de fundos de investimento e fundos de
          previdência.
        </span>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-jost text-sm">
          <div className="flex gap-4">
            <div className="w-64">
              <Input
                name="description"
                label="Nome da base"
                value={form.description}
                onChange={handleChange}
                required
              />
            </div>

            <div className="w-72">
              <Select
                name="baseType"
                label="Tipo da base"
                value={form.baseType}
                onChange={handleChange}
                options={[
                  { label: 'Fundos de Investimento', value: 'investment_funds' },
                  { label: 'Fundos de Previdência', value: 'pension_funds' },
                ]}
                required
              />
            </div>

            <div className="w-20">
              <ColorSelect name="color" label="Cor" value={form.color} onChange={handleChange} required />
            </div>
          </div>

          {/* Dropzone clicável */}
          <div
            role="button"
            tabIndex={0}
            onClick={openFilePicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') openFilePicker();
            }}
            className="flex flex-col items-center justify-center border-dashed border border-tealSteel rounded-lg h-56 sm:h-64 md:h-72 text-blueSteelDark gap-4 md:gap-5 text-center px-4 cursor-pointer hover:bg-blueExtraPale/30 transition"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFiles = Array.from(e.dataTransfer.files || []);
              setFileSafe(droppedFiles[0]);
            }}
          >
            <span className="rounded-full bg-blueExtraPale p-6 md:p-8">
              <FileText size={48} strokeWidth={1.5} className="md:hidden" />
              <FileText size={60} strokeWidth={1.5} className="hidden md:block" />
            </span>

            {fileToUpload ? (
              <span className="text-base md:text-lg font-medium">Arquivo selecionado: {fileToUpload.name}</span>
            ) : (
              <>
                <span className="text-base md:text-lg font-medium">
                  Arraste e solte o arquivo ou clique para selecionar
                </span>

                <div className="flex flex-col items-center text-xs md:text-sm leading-snug">
                  <span>
                    Arquivos suportados: <span>xls, xlsx, csv</span>
                  </span>
                  <span>
                    Tamanho máximo permitido: <span>2mb</span>
                  </span>
                </div>
              </>
            )}

            {/* input file invisível */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,.csv"
              className="hidden"
              onChange={(e) => {
                const file = (e.target.files && e.target.files[0]) || null;
                setFileSafe(file);
                e.target.value = '';
              }}
            />
          </div>

          {/* Arquivo selecionado */}
          <div className="flex flex-col gap-2">
            {fileToUpload && (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-tealLight shrink-0" />
                  <span className="text-blueSteel text-xs md:text-sm italic truncate max-w-[70vw] md:max-w-none">
                    {fileToUpload.name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setFileToUpload(null)}
                  className="p-1 rounded hover:bg-red-50"
                  aria-label="Remover arquivo"
                  title="Remover arquivo"
                >
                  <X size={16} className="text-redSoft" />
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end items-center mt-1">
            <div className="flex justify-between gap-2">
              <SecondaryButton onClick={onClose} type="button">
                CANCELAR
              </SecondaryButton>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </CustomModal>
  );
};

export default AddBaseModal;
